import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Coffee } from 'lucide-react';
import { useShopSettingsLoad } from './useShopSettingsLoad';
import { createTagOrder, getTagOrder, type TagOrder } from '../../../services/tag-order-service';
import OwnerShell, { OwnerLoadingShell } from '../OwnerShell';
import CoralHeader from '../../dashboard/CoralHeader';

const INPUT_CLS = 'w-full p-3 bg-background rounded-[8px] border-2 border-hairline focus:border-coral outline-none text-primary text-sm font-grotesk placeholder:text-body-grey';
const LABEL_CLS = 'block font-heading text-[8px] text-body-grey mb-1 uppercase';

const AU_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

/** The three states a shop sees. `posted` is treated as "no open order" so
 *  the form reappears and a shop can order another tag for a second till. */
const STEPS: { key: TagOrder['status']; label: string; blurb: string }[] = [
  { key: 'ordered', label: 'ORDERED', blurb: 'We have your request.' },
  { key: 'encoded', label: 'ENCODED', blurb: 'Your tag is programmed for your shop.' },
  { key: 'posted', label: 'POSTED', blurb: 'On its way to you.' },
];

/**
 * Server error codes mapped to plain sentences a shop owner can act on.
 * getTagOrder/createTagOrder deliberately throw the server's own reason
 * rather than swallowing it (see tag-order-service.ts) — that convention
 * stays, but a raw token like "order_already_open" printed straight to the
 * screen reads as a bug report, not a sentence. Anything NOT in this map
 * still surfaces verbatim: swallowing an unrecognised reason is exactly the
 * failure mode that convention exists to prevent.
 */
const ERROR_MESSAGES: Record<string, string> = {
  order_already_open: 'You already have a tag order in progress.',
};

/**
 * "Order your NFC tag" — SETUP sub-screen.
 *
 * Lives here rather than on COUNTER QR (the other candidate) because a shop
 * with no tag has no reason to open Counter QR: that page is where you go to
 * PRINT something you already have. SETUP is the checklist a new shop is
 * already working through, and an un-ordered tag is an incomplete setup step.
 *
 * The shop never supplies its on-chain id even though the tag is encoded
 * against it — the server reads it from the shop row the owner is authenticated
 * against. Asking would be both redundant and a way to get it wrong.
 */
function TagOrderSettings() {
  const navigate = useNavigate();
  const { loading, shopId, shopName, shopRow } = useShopSettingsLoad();

  const [order, setOrder] = useState<TagOrder | null>(null);
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [quantity, setQuantity] = useState(1);
  const [contactName, setContactName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [street, setStreet] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('QLD');
  const [postcode, setPostcode] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!shopId) return;
    getTagOrder(shopId).then((o) => { setOrder(o); setChecking(false); });
  }, [shopId]);

  // Pre-fill the business name from the shop row — it is the same string in
  // every case, and a shop retyping its own name is a chance to typo it onto
  // a shipping label.
  useEffect(() => {
    if (shopRow?.name && !businessName) setBusinessName(shopRow.name);
  }, [shopRow, businessName]);

  async function handleSubmit() {
    if (!shopId) return;
    setError(null);
    setSubmitting(true);
    try {
      const created = await createTagOrder({
        shopId, quantity,
        contact_name: contactName, business_name: businessName,
        street, suburb, state, postcode, phone, notes,
      });
      setOrder(created);
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Order failed';
      setError(ERROR_MESSAGES[raw] ?? raw);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <OwnerLoadingShell active="setup" statusLabel="SETUP" title="Loading…">
        <div className="bg-white rounded-[10px] h-64 animate-pulse" />
      </OwnerLoadingShell>
    );
  }

  // Same guard as the other three settings sub-screens (RewardsSettings,
  // StorefrontSettings, CounterSettings). Must sit ahead of the `checking`
  // gate below: getTagOrder() is only ever called when shopId is truthy, so
  // `checking` never resolves without a shop — without this branch first,
  // a shopless owner (bookmark, back button, stale link) hits an infinite
  // loading skeleton with no way out.
  if (!shopId) {
    return (
      <OwnerLoadingShell active="setup" statusLabel="SETUP" title="No shop yet">
        <div className="bg-white rounded-[10px] text-center py-12">
          <Coffee size={40} className="mx-auto mb-4 text-coral" />
          <p className="font-heading text-[10px] text-body-grey">NO SHOP REGISTERED YET</p>
        </div>
      </OwnerLoadingShell>
    );
  }

  if (checking) {
    return (
      <OwnerLoadingShell active="setup" statusLabel="SETUP" title="Loading…">
        <div className="bg-white rounded-[10px] h-64 animate-pulse" />
      </OwnerLoadingShell>
    );
  }

  // A `posted` order is treated as "no open order" — the DB's partial unique
  // index (WHERE status <> 'posted'), the API's 409 rule, and the runbook all
  // already assume reordering is allowed once a tag has shipped. Without this,
  // `order ? tracker : form` never lets the form reappear and the "order
  // another for a second till" button (CounterSettings.tsx) is a dead end.
  const openOrder = order && order.status !== 'posted' ? order : null;
  const reachedIndex = openOrder ? STEPS.findIndex((s) => s.key === openOrder.status) : -1;

  return (
    <OwnerShell
      active="setup"
      header={<CoralHeader statusLabel="SETUP" eyebrow="NFC TAG" title={shopName} />}
    >
      <div className="space-y-3.5">
        <button
          onClick={() => navigate('/shops/settings')}
          className="font-grotesk font-bold text-[13px] text-body-grey hover:text-coral py-2 flex items-center gap-2"
        >
          <ArrowLeft size={13} /> Settings
        </button>

        {openOrder ? (
          <div className="bg-white rounded-[10px] p-[18px]">
            <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">
              {openOrder.quantity} tag{openOrder.quantity > 1 ? 's' : ''} on the way
            </h2>
            <p className="font-grotesk font-medium text-[13px] text-body-grey mb-3.5">
              Each tag is programmed for your shop before it ships, so it only ever
              stamps cards for you.
            </p>
            <ol className="space-y-2.5">
              {STEPS.map((step, i) => {
                const done = i <= reachedIndex;
                return (
                  <li
                    key={step.key}
                    className="flex items-start gap-3"
                    data-testid={`tag-step-${step.key}`}
                    data-done={done ? 'true' : 'false'}
                  >
                    <span
                      className={`mt-[2px] shrink-0 w-[14px] h-[14px] pixel-corners-sm ${done ? 'bg-coral' : 'bg-tab-inactive'}`}
                      aria-hidden
                    />
                    <span className="min-w-0">
                      <span className={`block font-heading text-[8px] ${done ? 'text-coral' : 'text-body-grey'}`}>
                        {step.label}
                      </span>
                      <span className="block font-grotesk font-medium text-[12px] text-body-grey">
                        {step.blurb}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        ) : (
          <>
            {/* order is non-null here only when the latest order is 'posted'
                (openOrder is null but order is not) — a shop reordering a tag
                for a second till, seeing the form again, deserves to know why
                rather than wondering if its first order was lost. */}
            {order && (
              <p className="font-grotesk font-medium text-[13px] text-body-grey px-1">
                Your last tag was posted
                {order.posted_at ? ` on ${new Date(order.posted_at).toLocaleDateString('en-AU')}` : ''}.
                {' '}Ordering below starts a new one.
              </p>
            )}
            <div className="bg-white rounded-[10px] p-[18px]">
            <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Order your NFC tag</h2>
            <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
              A tap-to-stamp tag for your counter — free, and we cover the postage.
              We program it for your shop before it ships.
            </p>

            <div className="space-y-2.5">
              <div>
                <label className={LABEL_CLS} htmlFor="tag-qty">How many</label>
                <input id="tag-qty" type="number" min={1} max={10} value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
                  className={INPUT_CLS} />
              </div>
              <div>
                <label className={LABEL_CLS} htmlFor="tag-contact">Your name</label>
                <input id="tag-contact" type="text" maxLength={80} value={contactName}
                  onChange={(e) => setContactName(e.target.value)} className={INPUT_CLS} />
              </div>
              <div>
                <label className={LABEL_CLS} htmlFor="tag-business">Business name</label>
                <input id="tag-business" type="text" maxLength={80} value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)} className={INPUT_CLS} />
              </div>
              <div>
                <label className={LABEL_CLS} htmlFor="tag-street">Street address</label>
                <input id="tag-street" type="text" maxLength={120} value={street}
                  onChange={(e) => setStreet(e.target.value)} className={INPUT_CLS} />
              </div>
              <div>
                <label className={LABEL_CLS} htmlFor="tag-suburb">Suburb</label>
                <input id="tag-suburb" type="text" maxLength={80} value={suburb}
                  onChange={(e) => setSuburb(e.target.value)} className={INPUT_CLS} />
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className={LABEL_CLS} htmlFor="tag-state">State</label>
                  <select id="tag-state" value={state}
                    onChange={(e) => setState(e.target.value)} className={INPUT_CLS}>
                    {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL_CLS} htmlFor="tag-postcode">Postcode</label>
                  <input id="tag-postcode" type="text" inputMode="numeric" maxLength={4}
                    value={postcode} onChange={(e) => setPostcode(e.target.value)}
                    className={INPUT_CLS} />
                </div>
              </div>
              <div>
                <label className={LABEL_CLS} htmlFor="tag-phone">Phone</label>
                <input id="tag-phone" type="tel" maxLength={30} value={phone}
                  onChange={(e) => setPhone(e.target.value)} className={INPUT_CLS} />
              </div>
              <div>
                <label className={LABEL_CLS} htmlFor="tag-notes">Delivery notes (optional)</label>
                <input id="tag-notes" type="text" maxLength={280} value={notes}
                  onChange={(e) => setNotes(e.target.value)} className={INPUT_CLS} />
              </div>
            </div>

            {/* Said at the point of collection, not buried in a policy page. */}
            <p className="font-grotesk font-medium text-[12px] text-body-grey mt-3">
              We use this address only to post your tag. We don't share it.
            </p>

            {error && (
              <p className="font-grotesk font-medium text-[13px] text-error mt-2">{error}</p>
            )}

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="mt-3.5 rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-6 disabled:opacity-50"
            >
              {submitting ? 'Sending…' : 'Order my tag'}
            </button>
            </div>
          </>
        )}
      </div>
    </OwnerShell>
  );
}

export default TagOrderSettings;
